import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { names } from 'Constants';
import {
	LoadingSpinner,
	ModalBoxSectionEditProfile,
	ReactSwitch,
	SectionHeadline,
	Button,
	FontAwesome,
	Collapsible,
	ProfileJobCard
} from 'Elements';
import stylesProfile from 'partials/homepage/section/profile-hero-header.module.scss';
import { useModal } from 'src/hooks/useModal';
import { Colors } from 'Types';
import { useProfileJobs } from 'src/hooks/useJob';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Props = {
	isLoadingProfile: boolean;
	isFetchingProfile: boolean;
	onChangeValue: (
		objKey: string,
		key: string,
		value: string | boolean
	) => void;
	jobs: {
		enabled: boolean;
		headline: string;
	};
};

export const BusinessPartnerJobSection = ({
	isLoadingProfile,
	onChangeValue,
	jobs
}: Props) => {
	const { t } = useTranslation();
	const perRow = 3;
	const navigate = useNavigate();
	const [next, setNext] = useState(perRow);
	const [loading, setLoading] = useState(false);
	const [openModal, toggleModal] = useModal();
	const {
		jobs: { add }
	} = names;

	const addJobUrl = add?.path as string;
	const { data, isLoading } = useProfileJobs({
		status: 1,
		pageSize: 20,
		pageNumber: 1,
		keyword: '',
		kind: 0
	});

	const handleMore = () => {
		setLoading(true);
		setNext(next + perRow);
	};

	const handleNavigate = () => {
		navigate(addJobUrl, { state: { prevPath: '/homepage' } });
	};

	useEffect(() => {
		setLoading(false);
	}, [next]);

	return (
		<section className="position-relative mb-5">
			<Container>
				{isLoadingProfile && isLoading && (
					<div className="d-flex align-items-center justify-content-center">
						<LoadingSpinner />
					</div>
				)}
				{!isLoadingProfile && jobs && !isLoading && (
					<div className="position-relative">
						<Collapsible
							header={
								<div
									className={`d-flex flex-wrap gap-3 align-items-center justify-content-between mb-3 ${stylesProfile.switcherBox}`}>
									<div className="d-flex flex-wrap align-items-center gap-3">
										<h5 className="fs-5 fw-700 d-flex align-items-center">
											{' '}
											{t('menu.jobs')}
										</h5>
										<ReactSwitch
											size="md"
											checked={jobs?.enabled}
											onChange={(val: boolean) => {
												onChangeValue(
													'jobs',
													'enabled',
													val
												);
											}}
										/>
										<span className="d-flex align-items-center mb-0 py-0 cursor-pointer">
											{t('forms.show-jobs-in-home')}
										</span>
									</div>
									<div>
										<Button
											size="sm"
											color={Colors['white-gray']}
											onClick={() => toggleModal()}>
											<FontAwesome
												icon="circle-info"
												size="1x"
												className="text-gray me-1"
											/>
											{t('button.more')}
										</Button>
									</div>
								</div>
							}>
							{!loading && (
								<>
									<SectionHeadline
										title={jobs?.headline}
										titlePlaceholder={`[${t(
											'placeholder.job-list-headline'
										)}]`}
										titleColor="text-gray-5"
										className="mx-auto text-center"
										onUpdateTitle={(val) =>
											onChangeValue(
												'jobs',
												'headline',
												val
											)
										}
									/>
									{data && data?.items?.length > 0 && (
										<Row className="justify-content-center">
											{data.items
												?.slice(0, next)
												?.map((job) => {
													return (
														<Col
															md={6}
															lg={4}
															key={job.id}
															className="mb-4">
															<ProfileJobCard
																type="communityPartner"
																job={job}
															/>
														</Col>
													);
												})}
										</Row>
									)}
									<div className="d-flex align-items-center justify-content-center mt-3 gap-3">
										{data && data.items?.length > next && (
											<Button
												size="lg"
												color={Colors['border-gray-4']}
												label={
													t(
														'button.more-jobs'
													) as string
												}
												className="rounded-pill"
												onClick={handleMore}
											/>
										)}
										{data && data?.totalItems > 0 && (
											<Button
												size="lg"
												className="gap-3 rounded-pill"
												onClick={handleNavigate}>
												<FontAwesome
													icon="plus"
													size="xl"
													swapOpacity
												/>
												{t('title.add-new-job')}
											</Button>
										)}
									</div>
									{data && data?.items?.length === 0 && jobs && (
										<div className="d-flex align-items-center justify-content-center mt-4 mt-md-5">
											<Button
												size="lg"
												className="gap-3 rounded-pill"
												onClick={handleNavigate}>
												<FontAwesome
													icon="plus"
													size="xl"
													swapOpacity
												/>
												{t('title.add-new-job')}
											</Button>
										</div>
									)}
								</>
							)}
						</Collapsible>
					</div>
				)}
				{!jobs?.enabled && (
					<div className="overlay overlay_white overlay-box" />
				)}

				<ModalBoxSectionEditProfile
					isOpen={openModal}
					onClose={toggleModal}
					title="info.jobs-title"
					description="info.jobs-description"
				/>
			</Container>
		</section>
	);
};
